/**
 * ContextualPrompt.tsx
 *
 * Base component for all progressive-profile prompts.
 * Slides up from the bottom — non-blocking, dismissible, lightweight.
 * Never full-screen. Never modal-blocking.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

export interface ContextualPromptProps {
  show: boolean;
  title: string;
  subtitle?: string;
  emoji?: string;
  onDismiss: () => void;
  children: React.ReactNode;
  /** Optional footer text, e.g. "Takes 5 seconds" */
  footerNote?: string;
  /** Controls placement — 'bottom' (default) or 'inline' */
  placement?: "bottom" | "inline";
  /** Unique ID for analytics */
  promptId?: string;
}

import type { Variants } from "framer-motion";

const slideUp: Variants = {
  hidden: { y: 120, opacity: 0, scale: 0.97 },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: { type: "spring" as const, stiffness: 300, damping: 30 },
  },
  exit: {
    y: 100,
    opacity: 0,
    scale: 0.96,
    transition: { duration: 0.22, ease: "easeIn" as const },
  },
};

export const ContextualPrompt: React.FC<ContextualPromptProps> = ({
  show,
  title,
  subtitle,
  emoji,
  onDismiss,
  children,
  footerNote,
  placement = "bottom",
}) => {
  if (placement === "inline") {
    return (
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, y: 10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -6, height: 0 }}
            transition={{ duration: 0.28, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className="relative rounded-2xl border border-tok-accent-primary/20 bg-[var(--bg-secondary,var(--bg-surface))] p-4 mt-3">
              <button
                onClick={onDismiss}
                className="absolute top-3 right-3 text-[var(--text-muted)] hover:text-[var(--text-muted)] transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="flex items-start gap-3 mb-3 pr-5">
                {emoji && <span className="text-xl leading-none mt-0.5">{emoji}</span>}
                <div>
                  <p className="text-sm font-bold text-[var(--text-primary)] leading-tight">{title}</p>
                  {subtitle && (
                    <p className="text-[11px] text-[var(--text-muted)] mt-0.5 leading-snug">{subtitle}</p>
                  )}
                </div>
              </div>

              {children}

              {footerNote && (
                <p className="text-[10px] text-[var(--text-muted)] mt-3 text-center">{footerNote}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Bottom-sheet placement
  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Scrim — semi-transparent, clicking dismisses */}
          <motion.div
            key="scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onDismiss}
            className="fixed inset-0 z-modal folio-modal-overlay"
          />

          {/* Sheet — centered on desktop, bottom-anchored on mobile */}
          <motion.div
            key="sheet"
            variants={slideUp}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-x-0 bottom-0 sm:inset-0 z-modal flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0 pointer-events-none"
          >
          <div className="w-full max-w-lg pointer-events-auto">
            <div className="relative rounded-3xl border border-line bg-[var(--bg-surface)] shadow-2xl overflow-hidden">
              {/* Accent glow at top */}
              <div className="bg-ember absolute top-0 left-1/2 -translate-x-1/2 w-32 h-[2px] opacity-60" />

              {/* Header */}
              <div className="flex items-start justify-between px-5 pt-5 pb-3">
                <div className="flex items-start gap-3">
                  {emoji && (
                    <span className="text-2xl leading-none mt-0.5 select-none">{emoji}</span>
                  )}
                  <div>
                    <p className="text-base font-black text-[var(--text-primary)] leading-tight tracking-tight">
                      {title}
                    </p>
                    {subtitle && (
                      <p className="text-[12px] text-[var(--text-muted)] mt-1 leading-snug">{subtitle}</p>
                    )}
                  </div>
                </div>

                <button
                  onClick={onDismiss}
                  className="flex-shrink-0 p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-muted)] hover:bg-surface-hover transition-all ml-3"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Content */}
              <div className="px-5 pb-2">{children}</div>

              {/* Footer note */}
              {footerNote && (
                <p className="text-[10px] text-[var(--text-muted)] text-center pb-4 pt-1">{footerNote}</p>
              )}
            </div>
          </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ContextualPrompt;
